import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Eyebrow, Empty } from "@/components/admin/ui";
import { Download, Loader2, Pencil, Play, Trash2, Video, X } from "lucide-react";

const fmtSize = (b) => (!b ? "—" : b > 1e9 ? `${(b / 1e9).toFixed(2)} GB` : `${Math.max(1, Math.round(b / 1e6))} MB`);
const fmtDur = (s) => (!s ? "—" : `${Math.floor(s / 60)}m ${s % 60}s`);
const STATUS = {
  complete: ["Ready", "text-emerald-300 bg-emerald-500/10 ring-emerald-400/30"],
  active: ["Recording", "text-red-300 bg-red-500/10 ring-red-400/30"],
  starting: ["Starting", "text-amber-300 bg-amber-500/10 ring-amber-400/30"],
  ending: ["Finishing", "text-amber-300 bg-amber-500/10 ring-amber-400/30"],
  failed: ["Failed", "text-white/40 bg-white/5 ring-white/10"],
  aborted: ["Aborted", "text-white/40 bg-white/5 ring-white/10"],
};

export function RecordingsCard({ roomName }) {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [playing, setPlaying] = useState(null);
  const allClasses = !roomName;

  const load = () => {
    api
      .get(allClasses ? "/recordings" : `/meetings/${roomName}/recordings`)
      .then(({ data }) => setItems(data?.recordings || []))
      .catch(() => setItems([]));
  };

  useEffect(load, [roomName]);

  const open = async (rec, mode) => {
    setBusyId(rec.id);
    try {
      const { data } = await api.get(
        `/recordings/${rec.id}/link${mode === "download" ? "?download=1" : ""}`
      );
      if (mode === "play") setPlaying(data.url);
      else {
        // The signed URL now carries an attachment disposition, so this saves
        // the file straight away instead of opening the player.
        const a = document.createElement("a");
        a.href = data.url;
        a.download = data.filename || "class-recording.mp4";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("Download started");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const rename = async (rec) => {
    const current = rec.display_name || "";
    const next = window.prompt("Name this recording — the downloaded file will use this name", current);
    if (next === null) return;
    const name = next.trim();
    if (!name) return toast.error("Give the recording a name");
    setBusyId(rec.id);
    try {
      const { data } = await api.patch(`/recordings/${rec.id}`, { name });
      setItems((list) =>
        list.map((r) => (r.id === rec.id ? { ...r, display_name: data.display_name } : r))
      );
      toast.success(`Renamed to “${data.display_name}”`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (rec) => {
    if (!window.confirm("Delete this recording? The MP4 is removed from your storage bucket too — this can't be undone.")) return;
    setBusyId(rec.id);
    try {
      await api.delete(`/recordings/${rec.id}`);
      setItems((list) => list.filter((r) => r.id !== rec.id));
      setPlaying(null);
      toast.success("Recording deleted from the panel and your bucket");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className="p-5" testid="report-recordings">
      <div className="flex items-center justify-between mb-4">
        <Eyebrow>Cloud recordings</Eyebrow>
        <button onClick={load} className="text-[10.5px] uppercase tracking-[0.16em] text-white/35 hover:text-white transition-colors" data-testid="recordings-refresh">
          Refresh
        </button>
      </div>

      {playing && (
        <div className="relative mb-5" data-testid="recording-player-wrap">
          <video
            src={playing}
            controls
            autoPlay
            className="w-full rounded-2xl ring-1 ring-white/10 bg-black"
            data-testid="recording-player"
          />
          <button
            onClick={() => setPlaying(null)}
            className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full bg-black/75 ring-1 ring-white/20 text-white/80 hover:text-white hover:bg-black transition-colors"
            data-testid="recording-player-close"
            aria-label="Close player"
            title="Close player"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {items === null ? (
        <div className="py-8 grid place-items-center">
          <Loader2 className="w-4 h-4 text-white/25 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <Empty icon={Video} text="No recordings yet — start one from Host controls during a live class." testid="recordings-empty" />
      ) : (
        <div className="space-y-2">
          {items.map((r) => {
            const [label, cls] = STATUS[r.status] || [r.status, "text-white/40 bg-white/5 ring-white/10"];
            const ready = r.status === "complete";
            return (
              <div
                key={r.id}
                className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.02] ring-1 ring-white/[0.06]"
                data-testid={`recording-row-${r.id}`}
              >
                <span className={`px-2 py-0.5 rounded-full text-[9.5px] uppercase tracking-[0.14em] ring-1 ${cls}`}>
                  {label}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] text-white/80 truncate">
                    {r.display_name ||
                      (allClasses ? r.class_title : new Date(r.started_at).toLocaleString())}
                  </div>
                  <div className="text-[10.5px] text-white/35">
                    {allClasses && `${new Date(r.started_at).toLocaleString()} · `}
                    {!allClasses && r.display_name && `${new Date(r.started_at).toLocaleString()} · `}
                    {r.part > 1 ? `Part ${r.part} · ` : ""}
                    {fmtDur(r.duration_seconds)} · {fmtSize(r.size_bytes)}
                  </div>
                </div>
                <button
                  onClick={() => rename(r)}
                  disabled={busyId === r.id}
                  className="w-9 h-9 grid place-items-center rounded-full ring-1 ring-white/12 text-white/45 hover:text-emerald-300 hover:ring-emerald-400/40 transition-colors disabled:opacity-50"
                  data-testid={`recording-rename-${r.id}`}
                  title="Rename — the downloaded file uses this name"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                {ready && (
                  <>
                    <button
                      onClick={() => open(r, "play")}
                      disabled={busyId === r.id}
                      className="w-9 h-9 grid place-items-center rounded-full ring-1 ring-white/12 text-white/70 hover:text-white hover:ring-white/30 transition-colors disabled:opacity-50"
                      data-testid={`recording-play-${r.id}`}
                      title="Play"
                    >
                      {busyId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => open(r, "download")}
                      disabled={busyId === r.id}
                      className="w-9 h-9 grid place-items-center rounded-full bg-emerald-400 text-black hover:-translate-y-0.5 transition-transform duration-200 disabled:opacity-50"
                      data-testid={`recording-download-${r.id}`}
                      title="Download MP4"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => remove(r)}
                  disabled={busyId === r.id}
                  className="w-9 h-9 grid place-items-center rounded-full ring-1 ring-white/12 text-white/40 hover:text-red-300 hover:ring-red-400/40 transition-colors disabled:opacity-50"
                  data-testid={`recording-delete-${r.id}`}
                  title="Delete from panel and storage"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
